import {
  call,
  delay,
  put,
  race,
  select,
  spawn,
  take,
  takeLeading,
} from "redux-saga/effects";

import {
  BattleRatingsData,
  CharacterBaseStats,
  CombatLogTypes,
  ControlsPayload,
  DamageModel,
  DerivedStatSets,
  Fighter,
  FightStatuses,
  MobDrops,
  SkillModel,
} from "types";

import { BASE_STATS } from "data/baseStats";
import { DERIVED_STATS } from "data/derivedStats";
import {
  calculateBattleRating,
  doesDodgeHappen,
  doesEscapeHappen,
  getBattleRatingData,
  getMedallionsGained,
  getNewlyUnlockedMobUpgrades,
  getOpponentBaseStatTarget,
  getRegularAttackValues,
  getTotalDamageValuesAfterDefense,
} from "libs/fight";
import { getChargedSkills, getSkillData } from "libs/skill";
import {
  calculateInitialMobStats,
  getAreaData,
  getBuildingData,
  getPlanetData,
} from "libs/stats";
import {
  activateSkill,
  addCharacterTurnPriority,
  addCombatLog,
  addEventLog,
  addLogMessage,
  addOpponentTurnPriority,
  continueFight,
  disableFightActions,
  disableMovement,
  enableFightActions,
  enableMovement,
  endFightLose,
  endFightWin,
  escapeFight,
  fightAgain,
  healOpponent,
  moveToPosition,
  onwardFightLose,
  onwardFightWin,
  previewFight,
  rechargeOpponentSkillsTurn,
  rechargeSkillsTurn,
  resetAnimations,
  resetCombatLogs,
  resetDamage,
  resetFightResults,
  resetLastFightAction,
  resetOpponentAnimations,
  resetOpponentDamage,
  resetOpponentParts,
  resetOpponentSkillsRecharge,
  resetOpponentSkillsValues,
  resetSkillsRecharge,
  resetSkillsValues,
  resetTurnPriorities,
  restoreAllParts,
  retreatFight,
  setFightResults,
  setFightStatus,
  setLastFightAction,
  setMobBattleRating,
  setupOpponent,
  showMessage,
  startFight,
  startMobDialog,
  targetReactor,
  targetShields,
  targetTargetingComputer,
  targetThrusters,
  targetWeapons,
  turnShip,
} from "redux/actions";
import { enterAreaSaga, healSaga } from "redux/sagas/character";
import {
  getBattleRatings,
  getCurrentBaseStats,
  getDerivedStats,
  getLocationInfo,
  getPilotSkills,
  getShipHealth,
  getUserInfo,
} from "redux/selectors/characterSelectors";
import {
  getCharacterDamage,
  getCharacterFighterData,
  getFightStatus,
  getFightTurnsInfo,
  getOpponentBaseInfo,
  getOpponentDamage,
  getOpponentDerivedStats,
  getOpponentDrops,
  getOpponentFighterData,
  getOpponentHealth,
  getOpponentPilotSkills,
  getOpponentSkillsFightInfo,
  getOpponentSlug,
  getOpponentTargets,
  getSkillsFightInfo,
} from "redux/selectors/fightSelectors";
import { activateSkillSaga, setInitialSkillsRechargeSaga } from "./skill";

export const FIGHT_DISTANCE = 30;
export const ESCAPE_DISTANCE = 40;
export const FIGHT_ANIMATIONS = {
  attack: 300,
  damaged: 300,
  weaken: 500,
  dodge: 300,
  nullify: 500,
  escape: 500,
  lose: 3500,
};

function* startMobDialogSaga({ payload }: { payload: string }) {
  // Set up opponent for fight so we can see their stats
  yield call(setupOpponentSaga, payload);

  // Show opening dialog from Mob
  yield put(setFightStatus("openingDialog"));
}

function* previewFightSaga() {
  // Restrict movement of character
  yield put(disableMovement());

  // Reset skills recharging just in case
  yield put(resetSkillsRecharge());

  // Reset skills values just in case
  yield put(resetSkillsValues());

  // Show fight preview window
  yield put(setFightStatus("preview"));
}

function* retreatFightSaga() {
  // Enable character movement
  yield put(enableMovement());

  // Show fight preview window
  yield put(setFightStatus("notFighting"));
}

// startFightSaga
function* startFightSaga() {
  // Set fighting status
  yield put(setFightStatus("fighting"));

  // Set initial attack priority for character and opponent
  const { userName } = yield select(getUserInfo);
  const derivedStats: DerivedStatSets = yield select(getDerivedStats);
  const characterAttackSpeed = derivedStats.current.complete.attackSpeed;

  const { name: opponentName, position: opponentPosition } = yield select(
    getOpponentBaseInfo
  );
  const opponentDerivedStats: DerivedStatSets = yield select(
    getOpponentDerivedStats
  );
  const opponentAttackSpeed = opponentDerivedStats.current.complete.attackSpeed;

  yield put(addCharacterTurnPriority(characterAttackSpeed));
  yield put(addOpponentTurnPriority(opponentAttackSpeed));

  // Position ship in front of opponent and face to the right
  yield put(turnShip("right"));
  yield put(moveToPosition(opponentPosition - FIGHT_DISTANCE));

  // Set initial skills recharge for character and opponent
  const characterFighter: Fighter = yield select(getCharacterFighterData);
  const opponentFighter: Fighter = yield select(getOpponentFighterData);
  yield call(setInitialSkillsRechargeSaga, characterFighter);
  yield call(setInitialSkillsRechargeSaga, opponentFighter);

  yield put(
    addCombatLog({
      type: "start",
      fighter: null,
      message: `Battle begins between ${userName} and ${opponentName}`,
    })
  );

  // Get into fight saga
  yield call(fightSaga);
}

// continueFight (calls just fightSaga?)
function* continueFightSaga() {
  const status: FightStatuses = yield select(getFightStatus);

  if (status === "fighting") {
    // Get into fight saga
    yield call(fightSaga);
  }
}

function* fightAgainSaga() {
  const opponentSlug: string = yield select(getOpponentSlug);

  // Reset fight data
  yield call(resetFightSaga);

  // Set up opponent health again
  yield call(setupOpponentSaga, opponentSlug);

  // Start fight
  yield call(startFightSaga);
}

function* fightSaga() {
  // Set fight actions as disabled by default
  yield put(disableFightActions());

  // Fight
  while (true) {
    // Get new attack priority each turn, because it may change with weakened parts
    const derivedStats: DerivedStatSets = yield select(getDerivedStats);
    const characterAttackSpeed = derivedStats.current.complete.attackSpeed;

    const opponentDerivedStats: DerivedStatSets = yield select(
      getOpponentDerivedStats
    );
    const { isCharacterCurrentTurn } = yield select(getFightTurnsInfo);
    const opponentAttackSpeed =
      opponentDerivedStats.current.complete.attackSpeed;

    // check whose priority is higher, it's their turn to attack
    if (isCharacterCurrentTurn) {
      // CHARACTER'S TURN

      // Reset last fight action
      yield put(resetLastFightAction());

      yield put(enableFightActions());

      // Character attack sequence
      yield call(characterOptionsSaga);

      // Check if should end fight due to winning or escaping
      const status: FightStatuses = yield select(getFightStatus);
      const opponentHealth: number = yield select(getOpponentHealth);

      // Check if character has successfully escaped, then end fight
      if (status === "notFighting") {
        return false;
      }

      // Check if Opponent has died, then end fight
      if (opponentHealth === 0) {
        yield call(characterWinSaga);
        return false;
      }

      // Increase priority for opponent based on their speed
      yield put(addOpponentTurnPriority(opponentAttackSpeed));
    } else {
      // OPPONENT'S TURN

      // Give it a sec to sink in that it's the mob's turn
      yield delay(1000);

      // Opponent attack sequence
      yield call(opponentOptionsSaga);

      // Check if Characer has died, then end fight
      const health: number = yield select(getShipHealth);
      if (health === 0) {
        yield call(characterLoseSaga);
        return false;
      }

      // Increase priority for character based on their speed
      yield put(addCharacterTurnPriority(characterAttackSpeed));
    }

    // Every turn, tick down skills recharging turns for character and opponent
    yield put(rechargeSkillsTurn());
    yield put(rechargeOpponentSkillsTurn());

    // Enough time between turns so mob attack isn't immediate
    yield delay(500);
  }
}

function* setupOpponentSaga(payload: string) {
  const slug = payload;
  const { derivedStats } = calculateInitialMobStats(slug);

  // Set up health from max health, including boosts from mob upgrades
  const health = derivedStats.total.complete.maxHealth;

  // Save opponent data and stats into redux fight state
  yield put(
    setupOpponent({
      slug,
      health,
    })
  );
}

export function* characterOptionsSaga() {
  // Show options on mob to target

  // Wait for character to target a specific ship part
  const {
    weapons,
    shields,
    thrusters,
    targetingComputer,
    reactor,
    skill,
    escape,
  } = yield race({
    weapons: take(targetWeapons),
    shields: take(targetShields),
    thrusters: take(targetThrusters),
    targetingComputer: take(targetTargetingComputer),
    reactor: take(targetReactor),
    skill: take(activateSkill),
    escape: take(escapeFight),
  });
  const characterFighter: Fighter = yield select(getCharacterFighterData);
  const opponentFighter: Fighter = yield select(getOpponentFighterData);

  if (weapons) {
    yield put(disableFightActions());
    yield call(
      regularAttackSaga,
      characterFighter,
      opponentFighter,
      BASE_STATS.FIREPOWER
    );
  }
  if (shields) {
    yield put(disableFightActions());
    yield call(
      regularAttackSaga,
      characterFighter,
      opponentFighter,
      BASE_STATS.RESILIENCE
    );
  }
  if (thrusters) {
    yield put(disableFightActions());
    yield call(
      regularAttackSaga,
      characterFighter,
      opponentFighter,
      BASE_STATS.SPEED
    );
  }
  if (targetingComputer) {
    yield put(disableFightActions());
    yield call(
      regularAttackSaga,
      characterFighter,
      opponentFighter,
      BASE_STATS.PRECISION
    );
  }
  if (reactor) {
    yield put(disableFightActions());
    yield call(
      regularAttackSaga,
      characterFighter,
      opponentFighter,
      BASE_STATS.ENERGY
    );
  }
  if (skill) {
    try {
      yield put(disableFightActions());
      yield call(
        activateSkillSaga,
        characterFighter,
        opponentFighter,
        skill.payload
      );
    } catch (error: any) {
      yield put(showMessage(error.message));
    }
  }
  if (escape) {
    yield put(disableFightActions());
    yield call(escapeFightSaga);
  }
}

export function* fightControlsSaga({ payload }: { payload: ControlsPayload }) {
  const { areActionsEnabled } = yield select(getFightTurnsInfo);
  if (!areActionsEnabled) {
    return;
  }

  const { command } = payload;

  if (command.startsWith("shift+")) {
    const skills: SkillModel[] = yield select(getPilotSkills);
    const { skillsRecharge } = yield select(getSkillsFightInfo);
    const trainedSkills = skills.filter((skill) => !!skill.isTrained);

    const skillIndex = parseInt(command.split("+")[1]) - 1;

    if (skillIndex >= 0 && skillIndex < trainedSkills.length) {
      // Skills
      const { slug, name } = trainedSkills[skillIndex];
      const rechargeTurns = skillsRecharge[slug] || 0;
      const isRecharging = rechargeTurns > 0;

      if (isRecharging) {
        yield put(showMessage(`${name} is still recharging`));
      } else {
        yield put(setLastFightAction({ type: "skill", index: skillIndex }));
        yield put(activateSkill(slug));
      }
    }
  } else if (command === "esc") {
    yield put(setLastFightAction({ type: "tactic", index: 0 }));
    yield put(escapeFight());
  } else {
    // Regular attacks
    const ATTACK_COMMANDS = {
      "1": { action: targetWeapons, index: 0 },
      "2": { action: targetShields, index: 1 },
      "3": { action: targetThrusters, index: 2 },
      "4": { action: targetTargetingComputer, index: 3 },
      "5": { action: targetReactor, index: 4 },
    };

    const attackCommand =
      ATTACK_COMMANDS[command as keyof typeof ATTACK_COMMANDS];

    if (attackCommand) {
      yield put(
        setLastFightAction({ type: "attack", index: attackCommand.index })
      );
      yield put(attackCommand.action());
    }
  }
}

export function* opponentOptionsSaga() {
  const characterBaseStats: CharacterBaseStats = yield select(
    getCurrentBaseStats
  );
  const characterFighter: Fighter = yield select(getCharacterFighterData);

  const opponentTargets: (keyof CharacterBaseStats)[] = yield select(
    getOpponentTargets
  );
  const opponentFighter: Fighter = yield select(getOpponentFighterData);
  const skills: SkillModel[] = yield select(getOpponentPilotSkills);
  const { skillsRecharge } = yield select(getOpponentSkillsFightInfo);

  // Get base stat target from mob
  const baseStatWeakened = getOpponentBaseStatTarget(
    opponentTargets,
    characterBaseStats
  );

  // Check if skill is available to use, roll dice to see if it happens
  const chargedSkills = getChargedSkills(skills, skillsRecharge);
  if (chargedSkills.length > 0) {
    // If skills are available to use, use them
    yield call(
      activateSkillSaga,
      opponentFighter,
      characterFighter,
      chargedSkills[0].slug
    );
    return;
  }

  // Otherwise opponent does a regular attack
  yield call(
    regularAttackSaga,
    opponentFighter,
    characterFighter,
    baseStatWeakened
  );
}

export function* regularAttackSaga(
  attacker: Fighter,
  defender: Fighter,
  baseStatWeakened: keyof CharacterBaseStats
) {
  let attackDamage = 0;
  let attackWeakenParts = 0;
  let isDodge = false;
  let combatLogType: CombatLogTypes = "attack";
  let combatLogMessage = "";

  ({ attackDamage, attackWeakenParts, isDodge } = getRegularAttackValues(
    attacker.data.stats,
    defender.data.stats
  ));

  // Reduce attack damage by defender's shields for total damage
  const { damage, weakenParts, damageReduced, isNullified } =
    getTotalDamageValuesAfterDefense(
      attackDamage,
      attackWeakenParts,
      defender.data.stats.damageReduction,
      defender.data.stats.weakenPartsReduction
    );

  yield spawn(fightAnimation, attacker, "attack");
  // Let laser hit first before showing defender damage
  yield delay(100);

  yield put(
    attacker.actions.inflictDamage({
      damage,
      weakenParts,
      baseStatWeakened,
      totalBaseStats: defender.data.totalBaseStats,
      damageReduced,
    })
  );

  // ANIMATIONS / COMBAT LOGS

  // If damage dealt, animate defender getting hit
  if (damage > 0 || weakenParts > 0) {
    yield spawn(fightAnimation, defender, "damaged");
    combatLogMessage = `${attacker.name} attacked, inflicting <strong>${damage}</strong> total damage`;
    if (damageReduced > 0) {
      combatLogMessage += ` (${damageReduced} blocked)`;
    }
    if (baseStatWeakened) {
      combatLogMessage += ` and weakening ${baseStatWeakened} by <strong>${weakenParts}</strong>`;
    }
  }
  if (weakenParts > 0) {
    yield spawn(fightAnimation, defender, "weaken");
  }
  // Dodge animation
  if (isDodge) {
    yield spawn(fightAnimation, defender, "dodge");
    combatLogType = "dodge";
    combatLogMessage = `${attacker.name} attacked, but it was dodged`;
  }
  // If defender didn't dodge, but damage is still 0, highlight shields
  if (isNullified) {
    yield spawn(fightAnimation, defender, "nullify");
    combatLogType = "nullify";
    combatLogMessage = `${attacker.name} attacked, but it was completely nullified by shields (${damageReduced} blocked)`;
  }

  yield put(
    addCombatLog({
      type: combatLogType,
      fighter: attacker.name,
      message: combatLogMessage,
    })
  );
}

export function* skillAttackSaga(
  skillSlug: string,
  attacker: Fighter,
  defender: Fighter,
  skillDamage: number = 0,
  skillWeakenParts: number = 0,
  baseStatWeakened?: keyof CharacterBaseStats,
  ignoreDodge?: boolean,
  ignoreShields?: boolean
) {
  // Round damage values as initial starting point for damage
  const { maxAttackDamage: attackDamageInfo, maxWeakenParts: weakenPartsInfo } =
    DERIVED_STATS;

  // Set damage + weaken parts from skill damage
  let attackDamage = attackDamageInfo.rounder(skillDamage);
  let attackWeakenParts = weakenPartsInfo.rounder(skillWeakenParts);

  const skillInfo = getSkillData(skillSlug);
  let combatLogType: CombatLogTypes = "attack";
  let combatLogMessage = "";

  // Determine if dodge happens for skill attack
  let isDodge = false;
  if (!ignoreDodge) {
    isDodge = doesDodgeHappen(
      attacker.data.stats.attackAccuracy,
      defender.data.stats.attackEvasion
    );
  }

  if (isDodge) {
    // Defender dodges skill attack
    attackDamage = 0;
    attackWeakenParts = 0;
    yield spawn(fightAnimation, defender, "dodge");
    combatLogType = "dodge";
    combatLogMessage = `${attacker.name} used ${skillInfo.name}, but it was dodged`;
  }

  // Otherwise figure out damage
  let damage = attackDamage;
  let weakenParts = attackWeakenParts;
  let damageReduced = 0;
  let isNullified = false;

  // See if skill ignores defender's skills
  if (!ignoreShields) {
    // Reduce attack damage by defender's shields for total damage
    ({ damage, weakenParts, damageReduced, isNullified } =
      getTotalDamageValuesAfterDefense(
        attackDamage,
        attackWeakenParts,
        defender.data.stats.damageReduction,
        defender.data.stats.weakenPartsReduction
      ));
  }

  // Apply damage to opponent health and weaken base stats
  yield put(
    attacker.actions.inflictDamage({
      damage,
      weakenParts,
      baseStatWeakened,
      totalBaseStats: defender.data.totalBaseStats,
      damageReduced,
    })
  );

  // ANIMATIONS / COMBAT LOGS

  // If damage dealt, animate mob getting hit
  if (damage > 0 || weakenParts > 0) {
    yield spawn(fightAnimation, defender, "damaged");
  }
  if (weakenParts > 0) {
    yield spawn(fightAnimation, defender, "weaken");
  }
  // If mob didn't dodge, but damage is still 0, highlight shields
  if (isNullified) {
    yield spawn(fightAnimation, defender, "nullify");
    combatLogType = "nullify";
    combatLogMessage = `${attacker.name} used ${skillInfo.name}, but it was completely nullified by shields (${damageReduced} blocked)`;
  }
  // Show damage if not dodge or nullified, even if 0 on each
  if (!isDodge && !isNullified) {
    combatLogMessage = `${attacker.name} used ${skillInfo.name}, inflicting <strong>${damage}</strong> total damage`;
    if (damageReduced > 0) {
      combatLogMessage += ` (${damageReduced} blocked)`;
    }
    if (baseStatWeakened) {
      combatLogMessage += ` and weakening ${baseStatWeakened} by <strong>${weakenParts}</strong>`;
    }
  }

  yield put(
    addCombatLog({
      type: combatLogType,
      fighter: attacker.name,
      message: combatLogMessage,
    })
  );
}

export function* fightAnimation(
  fighter: Fighter,
  animation: keyof typeof FIGHT_ANIMATIONS
) {
  yield put(fighter.actions.startAnimation(animation));

  const animationTime = FIGHT_ANIMATIONS[animation];
  yield delay(animationTime);

  // Reset animation
  yield put(fighter.actions.endAnimation(animation));
}

function* escapeFightSaga() {
  // Calculate chance of fleeing
  const { userName } = yield select(getUserInfo);
  const derivedStats: DerivedStatSets = yield select(getDerivedStats);
  const characterFighter: Fighter = yield select(getCharacterFighterData);
  const characterStats = derivedStats.current.complete;

  const opponentDerivedStats: DerivedStatSets = yield select(
    getOpponentDerivedStats
  );
  const opponentStats = opponentDerivedStats.current.complete;

  // Animate escape attempt
  yield spawn(fightAnimation, characterFighter, "escape");
  yield delay(500);

  // Roll dice on fleeing
  const isEscape = doesEscapeHappen(
    characterStats.movementSpeed,
    opponentStats.movementSpeed
  );

  if (isEscape) {
    // Escape successful

    // End fight
    yield call(endFightEscapeSaga);

    // Message that it was successful
    yield put(showMessage(`You successfully escaped the battle`));
  } else {
    // Escape failed

    yield put(
      addCombatLog({
        type: "escape",
        fighter: userName,
        message: `${userName} attemped to escape, but failed`,
      })
    );
  }
}

export function* opponentHealSaga(healAmount?: number) {
  const health: number = yield select(getOpponentHealth);
  const derivedStats: DerivedStatSets = yield select(getOpponentDerivedStats);
  const currentMaxHealth = derivedStats.current.complete.maxHealth;

  // If max health is lower than current health, don't heal
  if (health > derivedStats.current.complete.maxHealth) {
    return;
  }

  // If no amount is passed through, heal to max health
  const healthGained = healAmount || currentMaxHealth;

  // Don't heal past max health
  const totalHealthGained = Math.min(currentMaxHealth - health, healthGained);

  yield put(healOpponent(totalHealthGained));
}

// End Fight Sagas

function* characterWinSaga() {
  const battleRatings: BattleRatingsData = yield select(getBattleRatings);
  const damageTaken: DamageModel[] = yield select(getCharacterDamage);

  // Get data from opponent
  const opponentSlug: string = yield select(getOpponentSlug);
  const { name: opponentName, medallions: opponentMedallions } = yield select(
    getOpponentBaseInfo
  );
  const drops: MobDrops = yield select(getOpponentDrops);
  const damageDealt: DamageModel[] = yield select(getOpponentDamage);
  const opponentFighter: Fighter = yield select(getOpponentFighterData);

  // Animate opponent losing
  yield delay(200);
  yield spawn(fightAnimation, opponentFighter, "lose");
  yield delay(1000);

  // Calculate Battle Performance - will affect exp, credits, drops
  const battleRating = calculateBattleRating(damageDealt, damageTaken);
  const battleRatingData = getBattleRatingData(battleRating);

  // If mob battle rating is better than previous battle, update it
  let shouldUpdateBattleRating = false;
  const previousBattleRating = battleRatings[opponentSlug];
  if (!previousBattleRating) {
    shouldUpdateBattleRating = true;
  } else {
    const previousBattleRatingData = getBattleRatingData(previousBattleRating);
    if (battleRatingData.value > previousBattleRatingData.value) {
      shouldUpdateBattleRating = true;
    }
  }

  let medallionsGained = 0;
  if (shouldUpdateBattleRating) {
    // Gain medallions based on new battle rating
    medallionsGained = getMedallionsGained(battleRating, previousBattleRating);

    yield put(setMobBattleRating({ mob: opponentSlug, rating: battleRating }));

    if (battleRating === "platinum") {
      // Add to achievements log, if platinum for the first time
      yield put(
        addLogMessage({
          type: "platinum",
          mobName: opponentName,
          mobMedallions: opponentMedallions,
        })
      );
    }
  }

  const dropsGained = getNewlyUnlockedMobUpgrades(
    drops,
    battleRating,
    previousBattleRating
  );

  // Show fight results
  yield put(setFightStatus("winResults"));
  yield put(
    setFightResults({
      battleRating: battleRating,
      medallions: medallionsGained,
      drops: dropsGained,
    })
  );

  // Restore all weakened parts so player can quick repair
  yield put(restoreAllParts());
  // Reset opponent weakened parts
  yield put(resetOpponentParts());

  // Event log
  yield put(
    addEventLog({
      event: "win_fight",
      eventParams: {
        mobName: opponentName,
        mobMedallions: opponentMedallions,
        battleRating,
      },
    })
  );
}

function* characterLoseSaga() {
  const characterFighter: Fighter = yield select(getCharacterFighterData);

  const { name: opponentName, medallions: opponentMedallions } = yield select(
    getOpponentBaseInfo
  );

  // Animate character losing
  yield delay(200);
  yield spawn(fightAnimation, characterFighter, "lose");
  yield delay(1000);

  // Show fight results
  yield put(setFightStatus("loseResults"));

  // Event log
  yield put(
    addEventLog({
      event: "lose_fight",
      eventParams: {
        mobName: opponentName,
        mobMedallions: opponentMedallions,
      },
    })
  );
}

function* onwardFightWinSaga() {
  // Reset all fight data
  yield call(resetFightSaga);

  // Enable character movement
  yield put(enableMovement());

  // Show last win dialog from mob
  yield put(setFightStatus("winDialog"));
}

function* endFightWinSaga() {
  // This may not be called, clicking win dialog is optional
  yield put(setFightStatus("notFighting"));
}

function* onwardFightLoseSaga() {
  // Keep character movement locked

  // Show lose dialog from mob
  yield put(setFightStatus("loseDialog"));
}

function* endFightLoseSaga() {
  // After clicking lose dialog, send character back to Bishop City healed
  yield put(setFightStatus("notFighting"));

  // Reset position back to shipworks at main city on loss
  const { planet } = yield select(getLocationInfo);

  const { isTutorial } = yield select(getOpponentBaseInfo);
  if (isTutorial) {
    yield put(enableMovement());
    return;
  }

  const currentPlanetInfo = getPlanetData(planet);
  const mainAreaInfo = getAreaData(currentPlanetInfo.mainArea);
  const shipworksSlug = mainAreaInfo.shipworks || "bishopShipworks";
  const shipworksInfo = getBuildingData(shipworksSlug);

  yield call(enterAreaSaga, mainAreaInfo.slug, shipworksInfo.position, "right");

  // Reset all fight data
  yield call(resetFightSaga);
}

function* endFightEscapeSaga() {
  // Turn ship to the left away from opponent
  yield put(turnShip("left"));

  // Move away from opponent
  const { position: opponentPosition } = yield select(getOpponentBaseInfo);
  yield put(moveToPosition(opponentPosition - ESCAPE_DISTANCE));

  // Reset all fight data
  yield call(resetFightSaga);

  // Enable character movement
  yield put(enableMovement());

  // Set status to not fighting
  yield put(setFightStatus("notFighting"));
}

function* resetFightSaga() {
  // Reset character damage
  yield put(resetDamage());

  // Restore all weakened parts
  yield put(restoreAllParts());

  // Full heal
  yield call(healSaga);

  // Reset skills recharging
  yield put(resetSkillsRecharge());

  // Reset skills values just in case
  yield put(resetSkillsValues());

  // Reset character animations
  yield put(resetAnimations());

  // Reset last fight action
  yield put(resetLastFightAction());

  // Reset opponent damage
  yield put(resetOpponentDamage());

  // Reset opponent weakened parts
  yield put(resetOpponentParts());

  // Reset opponent skills recharging
  yield put(resetOpponentSkillsRecharge());

  // Reset opponent skills values just in case
  yield put(resetOpponentSkillsValues());

  // Reset opponent animations
  yield put(resetOpponentAnimations());

  // Reset turn priorities
  yield put(resetTurnPriorities());

  // Reset fight results
  yield put(resetFightResults());

  // Reset combat logs
  yield put(resetCombatLogs());
}

export default function* fightSagas() {
  // Only one fight at a time
  yield takeLeading(startMobDialog, startMobDialogSaga);
  yield takeLeading(previewFight, previewFightSaga);
  yield takeLeading(retreatFight, retreatFightSaga);
  yield takeLeading(startFight, startFightSaga);
  yield takeLeading(continueFight, continueFightSaga);
  yield takeLeading(fightAgain, fightAgainSaga);
  yield takeLeading(onwardFightWin, onwardFightWinSaga);
  yield takeLeading(endFightWin, endFightWinSaga);
  yield takeLeading(onwardFightLose, onwardFightLoseSaga);
  yield takeLeading(endFightLose, endFightLoseSaga);
}
